import React from "react";
import { Check } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { cn } from "../../../lib/utils";
import { PlanType } from "@optidev/shared-types";

interface PricingCardProps {
  name: string;
  description: string;
  monthlyPrice: number | string;
  yearlyPrice: number | string;
  features: string[];
  featureHeader?: string;
  monthlyCredits?: number;
  isAnnual: boolean;
  highlighted?: boolean;
  ctaText?: string;
  onUpgrade: () => void;
  loading?: boolean;
  currentPlanType: PlanType;
  currentInterval: "month" | "year" | null;
  planType: PlanType;
  currencySymbol?: string;
  disabled?: boolean;
}

export const PricingCard: React.FC<PricingCardProps> = ({
  name,
  description,
  monthlyPrice,
  yearlyPrice,
  features,
  featureHeader,
  monthlyCredits,
  isAnnual,
  highlighted = false,
  ctaText,
  onUpgrade,
  loading = false,
  currentPlanType,
  currentInterval,
  planType,
  currencySymbol = "$",
  disabled = false,
}) => {
  const price = isAnnual ? yearlyPrice : monthlyPrice;
  const isCustomPrice = typeof price === "string";
  const displayedInterval = isAnnual ? "year" : "month";
  const yearlyAsMonthly =
    !isCustomPrice && typeof yearlyPrice === "number" ? yearlyPrice / 12 : null;

  // Plan hierarchy: free < standard < pro < enterprise (embed treated as standard-level)
  const PLAN_HIERARCHY: Record<string, number> = {
    [PlanType.FREE]: 0,
    [PlanType.STANDARD]: 1,
    [PlanType.PRO]: 2,
    [PlanType.ENTERPRISE]: 3,
    [PlanType.EMBED]: 1,
  };

  // Check if this is the exact current plan (same type AND same interval)
  const isCurrentPlan =
    currentPlanType === planType && currentInterval === displayedInterval;
  const isLowerPlan =
    PLAN_HIERARCHY[planType] < PLAN_HIERARCHY[currentPlanType];
  // Same plan but switching from annual to monthly is a downgrade (less value)
  const isSamePlanLowerInterval =
    currentPlanType === planType &&
    currentInterval === "year" &&
    displayedInterval === "month";
  const isDisabled =
    isCurrentPlan || isLowerPlan || isSamePlanLowerInterval || loading || disabled;

  return (
    <Card
      className={cn(
        "relative flex flex-col h-full transition-all duration-200 hover:shadow-md",
        "border-neutral-200 dark:border-neutral-800",
        isCurrentPlan && "!border-foreground/20 bg-foreground/[0.03] dark:bg-foreground/[0.06]",
        highlighted && "ring-2 ring-primary/20"
      )}
    >
      <CardHeader className="pb-3 px-4 pt-4">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-0.5">
            <CardTitle className="text-base font-semibold">{name}</CardTitle>
            <CardDescription className="text-xs text-muted-foreground">
              {description}
            </CardDescription>
          </div>
          {isCurrentPlan && (
            <span className="text-[11px] shrink-0 font-medium text-foreground/70 bg-foreground/10 px-2 py-0.5 rounded-md">
              Current
            </span>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex-1 px-4 pb-4">
        {/* Price */}
        <div className="mb-4">
          {isCustomPrice ? (
            <div className="text-2xl font-bold">{price}</div>
          ) : (
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-bold">
                {currencySymbol}
                {Number(isAnnual && yearlyAsMonthly !== null ? yearlyAsMonthly : price).toFixed(2)}
              </span>
              {isAnnual && yearlyAsMonthly !== null && typeof monthlyPrice === "number" && (
                <span className="text-base text-muted-foreground line-through">
                  {currencySymbol}{Number(monthlyPrice).toFixed(2)}
                </span>
              )}
              <span className="text-xs text-muted-foreground">/month</span>
            </div>
          )}
          {!isCustomPrice && isAnnual && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {currencySymbol}{Number(yearlyPrice).toFixed(2)}/year billed annually
            </p>
          )}
        </div>

        {/* Features */}
        <div className="space-y-2">
          {featureHeader && (
            <p className="text-xs text-muted-foreground mb-1">{featureHeader}</p>
          )}
          {monthlyCredits && (
            <div className="flex items-start gap-2">
              <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
              <span className="text-xs leading-relaxed">
                {monthlyCredits} credits per month
                {isAnnual && (
                  <span className="text-muted-foreground"> · refills monthly</span>
                )}
              </span>
            </div>
          )}
          {features.map((feature, index) => (
            <div key={index} className="flex items-start gap-2">
              <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
              <span className="text-xs leading-relaxed">{feature}</span>
            </div>
          ))}
        </div>
      </CardContent>

      <CardFooter className="pt-0 px-4 pb-4">
        <Button
          className={cn(
            "w-full h-9 text-sm font-medium",
            isCurrentPlan
              ? "bg-foreground/10 hover:bg-foreground/15 text-foreground border-0"
              : ctaText
                ? "bg-foreground hover:bg-foreground/90 text-background border-0"
                : !isDisabled
                  ? "bg-primary hover:bg-primary/90 text-primary-foreground"
                  : ""
          )}
          variant={isDisabled && !isCurrentPlan && !ctaText ? "outline" : "default"}
          onClick={onUpgrade}
          disabled={isDisabled}
        >
          {loading
            ? "Loading..."
            : isCurrentPlan
              ? "Current Plan"
              : isLowerPlan
                ? ""
                : ctaText || "Upgrade"}
        </Button>
      </CardFooter>
    </Card>
  );
};
